import React, { useState, useEffect, useCallback } from 'react';
import { hasPermission } from '../utils/permissions';
import { PERMISSIONS } from '../constants/permissions';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
import { LoadingSpinner, PageHeader, Button, Modal, ConfirmModal, FormInput, FormSelect } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { useNotification } from '../context/NotificationContext.jsx';

// --- Role Form (Super Admin Only) ---
const RoleForm = ({ role, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        name: role?.name || '',
        scope: role?.scope || 'Platform'
    });
    const handleChange = (e) => setFormData({ ...formData, [e.target.id]: e.target.value });
    const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };
    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <FormInput label="Role Name" id="name" value={formData.name} onChange={handleChange} required />
            <FormSelect label="Scope" id="scope" value={formData.scope} onChange={handleChange}>
                <option>Platform</option><option>Partner</option><option>Tenant</option>
            </FormSelect>
            <div className="flex justify-end gap-4 pt-4">
                <Button type="button" variant="secondary" onClick={onCancel} withShadow>Cancel</Button>
                <Button type="submit">{role ? 'Save Changes' : 'Create Role'}</Button>
            </div>
        </form>
    );
};

const ManageRolesTab = ({ token }) => {
    const { data: roles, refresh: refreshRoles } = useData('/roles', token);
    const { addNotification } = useNotification();

    const [roleModal, setRoleModal] = useState({ isOpen: false, role: null });
    const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, item: null });

    const handleSaveRole = async (roleData) => {
        const isEditing = !!roleModal.role;
        const endpoint = isEditing ? `/roles/${roleModal.role.id}` : '/roles';
        const method = isEditing ? 'PUT' : 'POST';
        try {
            await apiFetch(endpoint, token, { method, body: JSON.stringify(roleData) });
            addNotification(`Role "${roleData.name}" saved.`);
            refreshRoles();
        } catch (err) { addNotification(err.message, 'error'); }
        finally { setRoleModal({ isOpen: false, role: null }); }
    };

    const handleDelete = async () => {
        const { item } = confirmDelete;
        try {
            await apiFetch(`/roles/${item.id}`, token, { method: 'DELETE' });
            addNotification(`Role "${item.name}" deleted.`);
            refreshRoles();
        } catch (err) { addNotification(err.message, 'error'); }
        finally { setConfirmDelete({ isOpen: false, item: null }); }
    };

    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Manage Roles</h3>
                <Button onClick={() => setRoleModal({ isOpen: true, role: null })}>
                    <DynamicIcon name="PlusCircle" className="mr-2 h-4 w-4" />Add Role
                </Button>
            </div>
            <ul className="divide-y dark:divide-gray-700">
                {roles?.map(role => (
                    <li key={role.id} className="py-2 flex justify-between items-center">
                        <div>
                            <p className="font-semibold">{role.name}</p>
                            <p className="text-sm text-gray-500">{role.scope}</p>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="secondary" className="p-2" onClick={() => setRoleModal({ isOpen: true, role })}>
                                <DynamicIcon name="Edit" className="w-4 h-4" />
                            </Button>
                            <Button variant="danger" className="p-2" onClick={() => setConfirmDelete({ isOpen: true, item: role })}>
                                <DynamicIcon name="Trash2" className="w-4 h-4" />
                            </Button>
                        </div>
                    </li>
                ))}
            </ul>

            <Modal isOpen={roleModal.isOpen} onClose={() => setRoleModal({ isOpen: false, role: null })}
                title={roleModal.role ? 'Edit Role' : 'Create Role'}>
                <RoleForm role={roleModal.role} onSave={handleSaveRole} onCancel={() => setRoleModal({ isOpen: false, role: null })} />
            </Modal>

            <ConfirmModal isOpen={confirmDelete.isOpen} onClose={() => setConfirmDelete({ isOpen: false, item: null })}
                onConfirm={handleDelete} title="Delete Role">
                Are you sure you want to delete the "{confirmDelete.item?.name}" role? This cannot be undone.
            </ConfirmModal>
        </div>
    );
};

// --- Main Page ---
const RolesPermissionsPage = ({ token, currentUser, refreshUser }) => {
    const { data: initialRoles, isLoading: isLoadingRoles, refresh } = useData('/roles', token);
    const { data: permissions, isLoading: isLoadingPerms } = useData('/roles/permissions', token);

    const [roles, setRoles] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('assignments');
    const { addNotification } = useNotification();
    const isSuperAdmin       = hasPermission(currentUser, PERMISSIONS.ROLES_MANAGE_DEFINITIONS);
    const canAssignPermissions= hasPermission(currentUser, PERMISSIONS.ROLES_ASSIGN_PERMISSIONS);

    const ROLE_ORDER = { r1: 1, r2: 2, r6: 3, r5: 4, r3: 5, r8: 6, r9: 7, r10: 8, r4: 9, r7: 10 };

    useEffect(() => {
        if (initialRoles) {
            const sortedRoles = JSON.parse(JSON.stringify(initialRoles)).sort((a, b) => {
                const orderA = ROLE_ORDER[a.id?.toLowerCase()] ?? 99;
                const orderB = ROLE_ORDER[b.id?.toLowerCase()] ?? 99;
                return orderA - orderB || a.name.localeCompare(b.name);
            });
            setRoles(sortedRoles);
        }
    }, [initialRoles]);

    const isPermissionInScope = (permission, roleScope, admin) => {
        if (!permission?.category || !roleScope) return false;

        const adminRoleId = admin?.roleId?.toLowerCase?.() || '';
        const scope = roleScope.toLowerCase(); // 'platform', 'partner', 'tenant'

        // Super Admin and Platform Admin can do anything
        if (['r1', 'r2'].includes(adminRoleId)) return true;

        // Partner Admin can assign any permission they possess to a Partner or Tenant role
        // Tenant Admin can assign any permission they possess to a Tenant role
        if (adminRoleId === 'r3') return ['partner', 'tenant'].includes(scope);
        if (adminRoleId === 'r4') return scope === 'tenant';

        return false;
    };


    const canAdminEditRole = (admin, targetRole) => {
        if (!admin || !targetRole) return false;

        const adminRoleId = admin.roleId?.toLowerCase?.() || '';
        const targetRoleId = targetRole.id?.toLowerCase?.() || '';

        // Super Admin can edit any role
        if (adminRoleId === 'r1') return true;

        // Custom roles might not be in the hierarchy map, default them to -1
        const hierarchy = {
            r1: 10, r2: 9, r3: 8, r8: 7, r9: 6, r10: 5, r4: 4, r5: 3, r6: 2, r7: 1
        };

        const adminLevel = hierarchy[adminRoleId] ?? -1;
        const targetLevel = hierarchy[targetRoleId] ?? -1;

        // Rule: You cannot edit a role that is equal to or higher than your own level.
        // This strictly prevents "locking yourself out" by editing your own role's permissions.
        return adminLevel > targetLevel;
    };

    const handlePermissionChange = (roleId, permissionId) => {
        setRoles(currentRoles =>
            currentRoles.map(role => {
                if (role.id === roleId) {
                    const newPermissions = new Set(role.permissions);
                    newPermissions.has(permissionId)
                        ? newPermissions.delete(permissionId)
                        : newPermissions.add(permissionId);
                    return { ...role, permissions: Array.from(newPermissions) };
                }
                return role;
            })
        );
    };

    const handleSaveAll = async () => {
        setIsSaving(true);
        const changedRolesPayload = {};
        roles.forEach((role) => {
            const initialRole = initialRoles.find(r => r.id === role.id);
            if (JSON.stringify(role.permissions.sort()) !== JSON.stringify(initialRole.permissions.sort())) {
                changedRolesPayload[role.id] = role.permissions;
            }
        });

        if (Object.keys(changedRolesPayload).length === 0) {
            addNotification("No changes to save.", "info");
            setIsSaving(false);
            return;
        }

        try {
            // Collect all permission IDs that were visible/rendered to this admin.
            // The backend uses these to do a safe partial update, preserving any
            // out-of-scope permissions the UI didn't show (e.g. bundles:manage for Partner Admin).
            const allVisiblePermissionIds = permissionCategories
                .flatMap(([, perms]) => perms.map(p => p.id));
            
            await apiFetch(`/roles/permissions/bulk`, token, {
                method: 'PUT',
                body: JSON.stringify({ 
                    updates: changedRolesPayload,
                    provided_permission_ids: allVisiblePermissionIds.length > 0 ? allVisiblePermissionIds : null
                }),
            });
            addNotification("All changes saved successfully!");
            refresh();
            if (refreshUser) refreshUser();
        } catch (err) {
            addNotification(`Error saving changes: ${err.message}`, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoadingRoles || isLoadingPerms || !roles) return <LoadingSpinner />;

    const permissionCategories = permissions
        ? Object.entries(
            permissions.reduce((acc, perm) => {
                (acc[perm.category] = acc[perm.category] || []).push(perm);
                return acc;
            }, {})
        ).sort(([catA], [catB]) => catA.localeCompare(catB))
         .map(([category, perms]) => [category, perms.sort((a, b) => a.name.localeCompare(b.name))])
        : [];

    return (
        <div>
            <PageHeader title="Roles & Permissions" />

            <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                <nav className="-mb-px flex space-x-8">
                    <button onClick={() => setActiveTab('assignments')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'assignments' ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}>
                        Assignments
                    </button>
                    {isSuperAdmin && (
                        <button onClick={() => setActiveTab('manage')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'manage' ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}>
                            Manage Roles
                        </button>
                    )}
                </nav>
            </div>

            {activeTab === 'assignments' && (
                <>
                    {canAssignPermissions && (
                    <div className="flex justify-end mb-4">
                        <Button onClick={handleSaveAll} disabled={isSaving}>
                            {isSaving ? <DynamicIcon name="Loader2" className="animate-spin mr-2 h-4 w-4" /> : <DynamicIcon name="Save" className="mr-2 h-4 w-4" />}
                            Save All Changes
                        </Button>
                    </div>
                    )}

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="p-4 text-left font-semibold text-gray-600 dark:text-gray-300">Permission</th>
                                    {roles?.map(role => (
                                        <th key={role.id} className="p-4 text-center font-semibold text-gray-600 dark:text-gray-300">
                                            {role.name}
                                            <span className="text-xs text-gray-500 block">({role.scope})</span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {permissionCategories.map(([category, perms]) => (
                                    <React.Fragment key={category}>
                                        <tr className="bg-gray-100 dark:bg-gray-700/50">
                                            <td colSpan={(roles?.length || 0) + 1} className="p-2 px-4 font-bold text-gray-700 dark:text-gray-200">
                                                {category}
                                            </td>
                                        </tr>

                                        {perms.map(perm => (
                                            <tr key={perm.id}>
                                                <td className="p-4 text-gray-800 dark:text-gray-200">{perm.name}</td>
                                                {roles?.map(role => {
                                                    const isOutOfScope = !isPermissionInScope(perm, role.scope, currentUser);
                                                    const isNotEditableByAdmin = !canAdminEditRole(currentUser, role);
                                                    const isDisabled = isOutOfScope || isNotEditableByAdmin;

                                                    return (
                                                        <td key={`${role.id}-${perm.id}`} className="p-4 text-center">
                                                            <input
                                                                type="checkbox"
                                                                className={`h-5 w-5 rounded text-primary focus:ring-primary ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                                                checked={role.permissions.includes(perm.id)}
                                                                onChange={() => handlePermissionChange(role.id, perm.id)}
                                                                disabled={isDisabled}
                                                                title={isDisabled
                                                                    ? (isNotEditableByAdmin
                                                                        ? `You do not have permission to edit the '${role.name}' role.`
                                                                        : `This permission is out of scope for the '${role.scope}' role.`)
                                                                    : ''
                                                                }
                                                            />
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {activeTab === 'manage' && isSuperAdmin && <ManageRolesTab token={token} />}
        </div>
    );
};

export default RolesPermissionsPage;
